// Ops · Map health: traffic coverage, landmarks to review and map gaps (docs/NATIVE-MAPS-CONTRACT.md
// §3-4, contract.d.ts "Native maps"). The four JSON endpoints, the speeds CSV, the Manila hour of
// week, the landmark PATCH payload, the optimistic list update and the OpenStreetMap links. Pure
// (fetch and token come in as arguments): node --test runs it.
import { ApiRequestError } from '@popcart/core/v1';

/** @typedef {import('@popcart/core/v1').OpsLandmark} OpsLandmark */
/** @typedef {import('@popcart/core/v1').MapGap} MapGap */
/** @typedef {import('@popcart/core/v1').TrafficCoverage} TrafficCoverage */
/** @typedef {import('@popcart/core/v1').PlaceCategory} PlaceCategory */
/** @typedef {OpsLandmark['status']} LandmarkStatus */
/** @typedef {{ status?: 'verified' | 'hidden'; name?: string; category?: PlaceCategory }} LandmarkPatch */
/** @typedef {{ token: string | null; fetchImpl?: typeof fetch; baseUrl?: string; onUnauthorized?: () => void }} FetchOpts */

/** @type {readonly LandmarkStatus[]} */
export const LANDMARK_STATUSES = Object.freeze(['pending', 'verified', 'hidden']);

/** Every PlaceCategory (contract order) with the word Ops sees. */
export const CATEGORY_LABELS = /** @type {Readonly<Record<PlaceCategory, string>>} */ (
  Object.freeze({
    food: 'Food', cafe: 'Café', shop: 'Shop', grocery: 'Grocery', health: 'Health', school: 'School', worship: 'Worship',
    transit: 'Transit', bank: 'Bank', fuel: 'Fuel', gov: 'Government', park: 'Park', landmark: 'Landmark',
  })
);
export const PLACE_CATEGORIES = /** @type {PlaceCategory[]} */ (Object.keys(CATEGORY_LABELS));

// ── query keys + paths ───────────────────────────────────────────────────────────────────────
export const mapsKeys = {
  all: /** @type {const} */ (['ops', 'maps']),
  landmarks: /** @type {const} */ (['ops', 'maps', 'landmarks']),
  /** @param {LandmarkStatus} status */
  landmarksBy: (status) => /** @type {const} */ (['ops', 'maps', 'landmarks', status]),
  gaps: /** @type {const} */ (['ops', 'maps', 'gaps']),
  coverage: /** @type {const} */ (['ops', 'maps', 'traffic-coverage']),
};

export const OPS_MAPS_PATHS = {
  /** @param {LandmarkStatus} [status] */
  landmarks: (status) => `/ops/maps/landmarks${status ? `?status=${encodeURIComponent(status)}` : ''}`,
  /** @param {string} id */
  landmark: (id) => `/ops/maps/landmarks/${encodeURIComponent(id)}`,
  gaps: () => '/ops/maps/gaps',
  coverage: () => '/ops/maps/traffic-coverage',
  /** @param {number} hour */
  segmentSpeeds: (hour) => `/ops/maps/segment-speeds.csv?hour=${hour}`,
};

const NETWORK = () => new ApiRequestError(0, /** @type {any} */ ({ error: "Can't reach PopCart. Check your connection.", code: 'network' }));

/** A non-2xx as the core client would throw it (and the 401 hook). @param {Response} res @param {(() => void) | undefined} onUnauthorized */
async function failure(res, onUnauthorized) {
  if (res.status === 401) onUnauthorized?.();
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  return new ApiRequestError(res.status, body);
}

/**
 * The Ops map endpoints, in the core client's shape (bearer token, JSON, ApiRequestError). The core
 * client doesn't carry them yet; src/api.ts merges these under it, so the core's own methods win
 * once it does.
 * @param {{ baseUrl?: string; getToken: () => string | null; fetchImpl?: typeof fetch; onUnauthorized?: () => void }} opts
 */
export function createOpsMapsApi({ baseUrl = '', getToken, fetchImpl, onUnauthorized }) {
  const doFetch = fetchImpl ?? ((/** @type {Parameters<typeof fetch>} */ ...a) => fetch(...a));
  const root = baseUrl.replace(/\/+$/, '') + '/api/v1';
  /** @param {string} method @param {string} path @param {unknown} [body] */
  async function call(method, path, body) {
    /** @type {Record<string, string>} */
    const headers = { Accept: 'application/json' };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    let res;
    try {
      res = await doFetch(root + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
    } catch {
      throw NETWORK();
    }
    if (!res.ok) throw await failure(res, onUnauthorized);
    return res.json();
  }
  return {
    /** @param {LandmarkStatus} [status] @returns {Promise<OpsLandmark[]>} */
    opsMapLandmarks: (status) => call('GET', OPS_MAPS_PATHS.landmarks(status)),
    /** @param {string} id @param {LandmarkPatch} patch @returns {Promise<OpsLandmark>} */
    updateMapLandmark: (id, patch) => call('PATCH', OPS_MAPS_PATHS.landmark(id), patch),
    /** @returns {Promise<MapGap[]>} */
    opsMapGaps: () => call('GET', OPS_MAPS_PATHS.gaps()),
    /** @returns {Promise<TrafficCoverage>} */
    opsTrafficCoverage: () => call('GET', OPS_MAPS_PATHS.coverage()),
  };
}

/**
 * GET the typical speeds for one hour of the week as a CSV Blob (OSRM `--segment-speed-file`).
 * @param {number} hour 0..167
 * @param {FetchOpts} opts
 * @returns {Promise<Blob>}
 */
export async function fetchSegmentSpeedsCsv(hour, { token, fetchImpl, baseUrl = '', onUnauthorized }) {
  if (!Number.isInteger(hour) || hour < 0 || hour > 167) throw new RangeError('hour is 0 to 167');
  const doFetch = fetchImpl ?? ((/** @type {Parameters<typeof fetch>} */ ...a) => fetch(...a));
  /** @type {Record<string, string>} */
  const headers = { Accept: 'text/csv' };
  if (token) headers.Authorization = `Bearer ${token}`;
  let res;
  try {
    res = await doFetch(`${baseUrl.replace(/\/+$/, '')}/api/v1${OPS_MAPS_PATHS.segmentSpeeds(hour)}`, { method: 'GET', headers });
  } catch {
    throw NETWORK();
  }
  if (!res.ok) throw await failure(res, onUnauthorized);
  return res.blob();
}

/** "segment-speeds-h110.csv", as the API names it. @param {number} hour */
export const speedsFileName = (hour) => `segment-speeds-h${hour}.csv`;

// ── hour of week (Manila) ────────────────────────────────────────────────────────────────────
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');

/**
 * The hour of the week in Asia/Manila (UTC+8, no DST), the API's convention (lib/fleetTraffic.js
 * `hourOfWeek`, and the CSV's 400 message): Sunday 00:00-00:59 = 0 … Saturday 23:00 = 167.
 * @param {Date | number} [at]
 */
export function hourOfWeek(at = new Date()) {
  const w = new Date((at instanceof Date ? at.getTime() : Number(at)) + 8 * 3600 * 1000);
  return w.getUTCDay() * 24 + w.getUTCHours();
}

/** 110 → "Thursday 14:00–15:00". @param {number} h */
export function hourOfWeekLabel(h) {
  const hh = h % 24;
  return `${DAYS[Math.floor(h / 24)] ?? '?'} ${pad(hh)}:00–${pad((hh + 1) % 24)}:00`;
}

// ── landmarks ────────────────────────────────────────────────────────────────────────────────
/** Collapse spaces and trim, as the API keys names. @param {string} s */
export const cleanName = (s) => s.replace(/\s+/g, ' ').trim();

/**
 * The PATCH body for an edit: only what changed (a name with only spacing changes is no change).
 * Names are 2–60 characters, categories from the contract.
 * @param {Pick<OpsLandmark, 'name' | 'category'>} current
 * @param {{ name: string; category: string }} draft
 * @returns {{ ok: true; value: LandmarkPatch | null } | { ok: false; field: 'name' | 'category'; error: string }}
 */
export function landmarkEditPatch(current, draft) {
  const name = cleanName(draft.name);
  if (name.length < 2) return { ok: false, field: 'name', error: 'Give it a name people use, at least 2 letters.' };
  if (name.length > 60) return { ok: false, field: 'name', error: 'Keep the name under 60 characters.' };
  if (!PLACE_CATEGORIES.includes(/** @type {PlaceCategory} */ (draft.category))) return { ok: false, field: 'category', error: 'Pick a category.' };
  /** @type {LandmarkPatch} */
  const patch = {};
  if (name !== current.name) patch.name = name;
  if (draft.category !== current.category) patch.category = /** @type {PlaceCategory} */ (draft.category);
  return { ok: true, value: Object.keys(patch).length ? patch : null };
}

/** Approve (on everyone's map) or hide (off every map). @param {'approve' | 'hide'} action @returns {LandmarkPatch} */
export const landmarkStatusPatch = (action) => ({ status: action === 'approve' ? 'verified' : 'hidden' });

/**
 * The optimistic list for one status tab after a PATCH: an edit merges in place; a status change
 * takes the landmark off a tab it no longer belongs to (it lands on its new tab on the refetch).
 * @param {OpsLandmark[] | undefined} list
 * @param {LandmarkStatus} tab
 * @param {string} id
 * @param {LandmarkPatch} patch
 * @returns {OpsLandmark[] | undefined}
 */
export function patchLandmarkList(list, tab, id, patch) {
  if (!list) return list;
  if (patch.status && patch.status !== tab) return list.filter((l) => l.id !== id);
  return list.map((l) => (l.id === id ? { ...l, ...patch } : l));
}

/** "3 confirmations · 2 riders". @param {Pick<OpsLandmark, 'confirmations' | 'riders'>} l */
export function confirmationsLabel(l) {
  const c = `${l.confirmations} confirmation${l.confirmations === 1 ? '' : 's'}`;
  return `${c} · ${l.riders} rider${l.riders === 1 ? '' : 's'}`;
}

/** What happened, for the toast. @param {string} name @param {LandmarkPatch} patch */
export function landmarkToast(name, patch) {
  if (patch.status === 'verified') return `${name} is on every PopCart map now.`;
  if (patch.status === 'hidden') return `${name} is off the maps.`;
  return `Saved ${patch.name ?? name}.`;
}

// ── map links + geometry ─────────────────────────────────────────────────────────────────────
const fix6 = (/** @type {number} */ n) => String(Math.round(n * 1e6) / 1e6);

/** The OpenStreetMap editor at a point, zoom 19 (where a missing road is drawn). @param {{ lat: number; lng: number }} p */
export const osmEditUrl = (p) => `https://www.openstreetmap.org/edit#map=19/${fix6(p.lat)}/${fix6(p.lng)}`;

/** OpenStreetMap with a marker on the point, zoom 18 (to look at a landmark). @param {{ lat: number; lng: number }} p */
export const osmViewUrl = (p) => `https://www.openstreetmap.org/?mlat=${fix6(p.lat)}&mlon=${fix6(p.lng)}#map=18/${fix6(p.lat)}/${fix6(p.lng)}`;

/** "14.65012, 121.03004". @param {{ lat: number; lng: number }} p */
export const latLngLabel = (p) => `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}`;

/** The gaps as points for the map, sized by deliveries. @param {MapGap[]} gaps */
export function gapsGeoJSON(gaps) {
  return {
    type: /** @type {const} */ ('FeatureCollection'),
    features: gaps.map((g) => ({
      type: /** @type {const} */ ('Feature'),
      geometry: { type: /** @type {const} */ ('Point'), coordinates: /** @type {[number, number]} */ ([g.center.lng, g.center.lat]) },
      properties: { id: g.id, deliveries: g.deliveries, fixes: g.fixes },
    })),
  };
}

/**
 * [[minLng, minLat], [maxLng, maxLat]] around the points, padded so one point isn't a zero box.
 * @param {{ lat: number; lng: number }[]} pts
 * @returns {[[number, number], [number, number]] | null}
 */
export function pointsBounds(pts) {
  if (!pts.length) return null;
  const pad = 0.002;
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const p of pts) {
    minLng = Math.min(minLng, p.lng); maxLng = Math.max(maxLng, p.lng);
    minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat);
  }
  return [[minLng - pad, minLat - pad], [maxLng + pad, maxLat + pad]];
}
